"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations, translate } from "./translations";

export const SUPPORTED_LOCALES = ["de", "en"];
export const DEFAULT_LOCALE = "de";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(DEFAULT_LOCALE);

  useEffect(() => {
    let stored;
    try { stored = window.localStorage.getItem("nordlicht.locale"); } catch { /* Storage may be disabled. */ }
    const next = SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
    setLocaleState(next);
    document.documentElement.lang = next;
  }, []);

  const setLocale = (nextLocale) => {
    const next = SUPPORTED_LOCALES.includes(nextLocale) ? nextLocale : DEFAULT_LOCALE;
    setLocaleState(next);
    try { window.localStorage.setItem("nordlicht.locale", next); } catch { /* Keep the current session usable. */ }
    document.documentElement.lang = next;
  };

  const value = useMemo(() => ({
    locale,
    setLocale,
    t(key, fallback = key) {
      return translate(locale, key, fallback);
    },
    status(value) {
      const key = String(value || "UNKNOWN").toUpperCase();
      return translations[locale]?.[`status.${key}`]
        ?? translations[DEFAULT_LOCALE]?.[`status.${key}`]
        ?? value
        ?? translations[locale]?.["status.UNKNOWN"];
    },
    formatDate(value, options) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", options).format(date);
    },
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
